import { inject, Injectable, signal } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { KeyValueStorageService } from '@formbird/services';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private isDarkTheme = signal(false);
  private overlayContainer = inject(OverlayContainer);
  private keyValueStorageService = inject(KeyValueStorageService);

  private readonly THEME_KEY = 'APP_THEME';
  private readonly THEME_DARK = 'dark';
  private readonly THEME_LIGHT = 'light';

  constructor() {
    // Check for saved theme preference or default to light
    this.keyValueStorageService.getItem(this.THEME_KEY).then(result => {
      if (result) {
        this.setDarkTheme(result === this.THEME_DARK);
      } else {
        this.setDarkTheme(false);
      }
    });
  }

  /**
   * Get the current theme state
   */
  getTheme() {
    return this.isDarkTheme.asReadonly();
  }

  /**
   * Toggle between light and dark themes
   */
  toggleTheme(): void {
    this.setDarkTheme(!this.isDarkTheme());
  }

  /**
   * Set the theme explicitly
   * @param isDark - true for dark theme, false for light theme
   */
  setDarkTheme(isDark: boolean): void {
    this.isDarkTheme.set(isDark);
    
    const body = document.getElementsByTagName('body')[0];
    const overlayContainer = this.overlayContainer.getContainerElement();
    
    if (isDark) {
      body.classList.add('dark-theme');
      overlayContainer.classList.add('dark-theme');
      this.keyValueStorageService.setItem(this.THEME_KEY, this.THEME_DARK);
    } else {
      body.classList.remove('dark-theme');
      overlayContainer.classList.remove('dark-theme');
      this.keyValueStorageService.setItem(this.THEME_KEY, this.THEME_LIGHT);
    }
  }

  /**
   * Check if dark theme is currently active
   */
  isDarkMode(): boolean {
    return this.isDarkTheme();
  }
}
